import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function HowItWorksPage() {
  // Set document head using React 19 features
  useEffect(() => {
    document.title = 'How It Works - Sell Your Apartment Building Fast for Cash | SellApartmentsCash.com';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn how to sell your apartment building for cash in 3 simple steps. Get a fair cash offer in 24 hours, close on your timeline, and skip repairs. Our proven process makes selling multifamily properties fast and hassle-free.');

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'how to sell apartment building, sell multifamily property process, cash home buying process, sell commercial building steps, apartment building sale process');

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://sellapartmentscash.com/how-it-works');

    // Add structured data
    let existingScript = document.querySelector('script[type="application/ld+json"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Sell Your Apartment Building for Cash",
        "description": "Step-by-step guide to selling your multifamily property for cash quickly and easily",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Submit Property Information",
            "text": "Fill out our simple form with basic details about your apartment building or multifamily property"
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Receive Cash Offer",
            "text": "Get a fair, no-obligation cash offer within 24 hours based on current market conditions"
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "Close on Your Timeline",
            "text": "Choose your closing date and receive cash payment with no repairs, fees, or commissions"
          }
        ]
      });
      document.head.appendChild(script);
    }
  }, []); // Empty dependency array ensures this only runs once

  return (
    <div className="font-inter">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://public.readdy.ai/ai/img_res/7abae205adeb4e498789120770a9da68.jpg"
              alt="How it works process"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-center">
              How It Works: Sell Your Apartment Building for Cash
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto text-center">
              Our proven 3-step process makes selling your multifamily property simple, fast, and stress-free. No repairs, no fees, no hassle.
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-navy-900 mb-8 text-center">
                The Simple Process to <strong>Sell Your Apartment Building</strong>
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Selling your apartment building or multifamily property doesn't have to be complicated. At SellApartmentsCash.com, we've streamlined the entire process to make it as easy as possible for property owners to <strong>sell apartment buildings fast</strong> and receive cash offers without the traditional headaches of real estate transactions.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Whether you own a small duplex, a large apartment complex, or any commercial building in between, our process works the same way. We buy properties in any condition, any location, and we can close on your timeline. No real estate agents, no lengthy negotiations, no surprise fees at closing.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-500 p-6 my-8">
                  <p className="text-gray-800 font-semibold mb-2">Why Our Process Works:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>We have the cash ready to buy your property immediately</li>
                    <li>No waiting for buyer financing or loan approvals</li>
                    <li>We handle all paperwork and closing costs</li>
                    <li>Close in as little as 7 days or on your preferred timeline</li>
                    <li>No repairs or renovations required before sale</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step 1 */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-block bg-orange-500 text-white text-2xl font-bold rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    1
                  </div>
                  <h2 className="text-4xl font-bold text-navy-900 mb-6">
                    Submit Your <strong>Property Information</strong>
                  </h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    The first step to <strong>sell your multifamily property</strong> is incredibly simple. Fill out our quick online form with basic information about your apartment building. We need to know the property address, number of units, approximate square footage, and current condition. This takes just 2-3 minutes to complete.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Don't worry if you don't have all the exact details – we can work with estimates. We understand that property owners may not have every specification memorized, especially if you've inherited the property or own multiple buildings. Our team will follow up to gather any additional information needed to prepare your cash offer.
                  </p>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">What We Need to Know:</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Property location and address</strong> - helps us understand the local market</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Number of units</strong> - from duplexes to large apartment complexes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Property condition</strong> - we buy in any condition, even if it needs major repairs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Your timeline</strong> - when you'd like to close and receive your cash</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Current occupancy status</strong> - whether units are occupied or vacant</span>
                    </li>
                  </ul>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20person%20filling%20out%20online%20form%20on%20laptop%20computer%20in%20modern%20office%20with%20clean%20workspace%20showing%20digital%20property%20submission%20process%20for%20real%20estate%20transaction&width=800&height=900&seq=howitworks002&orientation=portrait"
                    alt="Submit property information online"
                    className="rounded-lg shadow-2xl w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step 2 */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="order-2 md:order-1 relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20cash%20offer%20document%20with%20calculator%20and%20financial%20analysis%20papers%20on%20desk%20showing%20fair%20market%20valuation%20for%20apartment%20building%20purchase%20with%20clean%20organized%20presentation&width=800&height=900&seq=howitworks003&orientation=portrait"
                    alt="Receive fair cash offer"
                    className="rounded-lg shadow-2xl w-full h-auto object-cover"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <div className="inline-block bg-orange-500 text-white text-2xl font-bold rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    2
                  </div>
                  <h2 className="text-4xl font-bold text-navy-900 mb-6">
                    Receive Your <strong>Fair Cash Offer</strong>
                  </h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Within 24 hours of submitting your property information, our experienced team will analyze your apartment building and prepare a fair, no-obligation cash offer. We use current market data, comparable sales, and property condition to determine a competitive offer price that reflects true market value.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Unlike traditional buyers who need financing approval, we have the cash ready to purchase your property immediately. Our offers are straightforward with no hidden fees, no commission charges, and no last-minute surprises. What we offer is what you'll receive at closing.
                  </p>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">Our Offer Includes:</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Fair market value assessment</strong> based on current conditions</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>No obligation to accept</strong> - take time to review and consider</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Transparent breakdown</strong> of how we calculated the offer</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Flexible closing timeline</strong> to match your needs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>All-cash purchase</strong> with no financing contingencies</span>
                    </li>
                  </ul>
                  <div className="bg-navy-50 border-l-4 border-navy-500 p-6 mt-8">
                    <p className="text-gray-800 font-semibold mb-2">No Pressure, No Obligation</p>
                    <p className="text-gray-700">
                      We want you to feel completely comfortable with your decision. Take all the time you need to review our offer, consult with advisors, or compare with other options. There's absolutely no pressure to accept.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step 3 */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-block bg-orange-500 text-white text-2xl font-bold rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    3
                  </div>
                  <h2 className="text-4xl font-bold text-navy-900 mb-6">
                    Close on <strong>Your Timeline</strong>
                  </h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Once you accept our offer, we move quickly to close the deal on your preferred timeline. Need to close in 7 days? We can do that. Need more time to relocate or make arrangements? That's fine too. We work around your schedule, not the other way around.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Our team handles all the paperwork, coordinates with the title company, and ensures a smooth closing process. You don't need to worry about repairs, staging, showings, or any of the typical hassles of selling real estate. We buy your <strong>apartment building as-is</strong> and take care of everything.
                  </p>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">What Happens at Closing:</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Sign the paperwork</strong> - simple, straightforward documents</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Transfer ownership</strong> - title company handles all legal requirements</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Receive your cash</strong> - immediate payment, no waiting for checks to clear</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <span><strong>Walk away stress-free</strong> - no repairs, no cleaning, no worries</span>
                    </li>
                  </ul>
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mt-8">
                    <p className="text-gray-800 font-semibold mb-2">We Cover All Closing Costs</p>
                    <p className="text-gray-700">
                      Unlike traditional sales where sellers pay 6% in agent commissions plus closing costs, we cover all fees. The offer we make is the amount you receive – no deductions, no surprises.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20closing%20ceremony%20with%20handshake%20and%20signed%20documents%20on%20table%20showing%20successful%20property%20transaction%20completion%20with%20keys%20and%20cash%20payment%20in%20modern%20office%20setting&width=800&height=900&seq=howitworks004&orientation=portrait"
                    alt="Close on your timeline"
                    className="rounded-lg shadow-2xl w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-navy-900 mb-12 text-center">
                Why Our Process Works Better Than <strong>Traditional Sales</strong>
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gray-50 p-8 rounded-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-lg mb-6">
                    <i className="ri-time-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">Speed</h3>
                  <p className="text-gray-700">
                    Traditional sales take 6-12 months on average. Our process takes as little as 7 days from offer to closing. No waiting for buyer financing, no deal falling through at the last minute.
                  </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-lg mb-6">
                    <i className="ri-money-dollar-circle-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">No Hidden Costs</h3>
                  <p className="text-gray-700">
                    Traditional sales cost 6-10% in commissions and fees. We cover all closing costs, so you keep more of your money. No agent commissions, no repair costs, no holding costs.
                  </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-lg mb-6">
                    <i className="ri-shield-check-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">Certainty</h3>
                  <p className="text-gray-700">
                    Traditional buyers need financing approval, which can fall through. We have cash ready and can close with certainty. Once you accept our offer, the deal is done.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Comparison */}
        <section className="py-20 bg-navy-900 text-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 text-center">
                Traditional Sale vs. Cash Sale Timeline
              </h2>
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold mb-6 text-orange-400">Traditional Sale (6-12 Months)</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">1</div>
                      <div>
                        <p className="font-semibold">Months 1-2: Prepare Property</p>
                        <p className="text-gray-300 text-sm">Make repairs, renovations, staging</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">2</div>
                      <div>
                        <p className="font-semibold">Months 3-4: List and Market</p>
                        <p className="text-gray-300 text-sm">Find agent, list property, show to buyers</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">3</div>
                      <div>
                        <p className="font-semibold">Months 5-8: Negotiate and Inspect</p>
                        <p className="text-gray-300 text-sm">Negotiate offers, inspections, more repairs</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">4</div>
                      <div>
                        <p className="font-semibold">Months 9-12: Financing and Closing</p>
                        <p className="text-gray-300 text-sm">Wait for buyer financing, hope deal doesn't fall through</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-6 text-orange-400">Our Cash Sale (7-30 Days)</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">1</div>
                      <div>
                        <p className="font-semibold">Day 1: Submit Information</p>
                        <p className="text-gray-300 text-sm">Fill out simple online form (2 minutes)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">2</div>
                      <div>
                        <p className="font-semibold">Day 2: Receive Offer</p>
                        <p className="text-gray-300 text-sm">Get fair cash offer within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">3</div>
                      <div>
                        <p className="font-semibold">Days 3-5: Accept and Schedule</p>
                        <p className="text-gray-300 text-sm">Accept offer and choose closing date</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">4</div>
                      <div>
                        <p className="font-semibold">Days 7-30: Close and Get Cash</p>
                        <p className="text-gray-300 text-sm">Sign papers, receive cash, done!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-navy-900 mb-12 text-center">
                Frequently Asked Questions About <strong>Our Process</strong>
              </h2>
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-navy-900 mb-3">
                    <a href="#faq-how-long" className="hover:text-orange-500">How long does the entire process take?</a>
                  </h4>
                  <p className="text-gray-700">
                    From the moment you submit your property information to receiving cash at closing, the process can take as little as 7 days. However, we're flexible and can work with your timeline if you need more time. Most transactions close within 2-4 weeks.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-navy-900 mb-3">
                    <a href="#faq-obligation" className="hover:text-orange-500">Am I obligated to accept your offer?</a>
                  </h4>
                  <p className="text-gray-700">
                    Absolutely not. Our cash offers are completely no-obligation. You can take time to review, compare with other options, or simply decline if it doesn't meet your needs. There's no pressure whatsoever.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-navy-900 mb-3">
                    <a href="#faq-repairs" className="hover:text-orange-500">Do I need to make any repairs before selling?</a>
                  </h4>
                  <p className="text-gray-700">
                    No repairs are necessary. We buy apartment buildings and multifamily properties in any condition. Whether your property needs minor cosmetic work or major structural repairs, we'll make an offer based on its current state.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-navy-900 mb-3">
                    <a href="#faq-costs" className="hover:text-orange-500">What costs do I have to pay?</a>
                  </h4>
                  <p className="text-gray-700">
                    We cover all closing costs, title fees, and transfer taxes. There are no real estate agent commissions (typically 6% of sale price), no repair costs, and no hidden fees. The offer we make is what you receive.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-navy-900 mb-3">
                    <a href="#faq-tenants" className="hover:text-orange-500">What if my property has tenants?</a>
                  </h4>
                  <p className="text-gray-700">
                    We buy properties with tenants in place. You do not need to evict anyone or wait for leases to expire. We will work with the existing tenant situation and handle everything after closing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-orange-500 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Submit your property information now and receive a fair cash offer within 24 hours. No obligation, no pressure, no hassle.
            </p>
            <a 
              href="/#get-offer"
              className="inline-block bg-white text-orange-500 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your Cash Offer Now
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}